import { useState, useCallback, useEffect } from 'react';
import { useSession } from '../../../../context/SessionContext';
import { apiClient } from '../../../../api/client';
import {
  transformArchitecture,
  transformCallGraph,
  transformFileDependencies,
  transformFileTree
} from '../utils/transformers';

/**
 * Hook to fetch and transform CKB data for graph visualization
 */
/**
 * Check if architecture response is module-level fallback (no directory data)
 */
function looksLikeModuleData(raw) {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const data = parsed?.data || parsed;
    const directories = data?.directories || [];
    const modules = data?.modules || [];
    // Module fallback: no directories, but has modules
    return directories.length === 0 && modules.length > 0;
  } catch {
    return false;
  }
}

export function useGraphData(vizType, targetSymbol = null, archMode = 'structure') {
  const { sessionId } = useSession();
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noDependencyData, setNoDependencyData] = useState(false);

  const fetchData = useCallback(async () => {
    if (!sessionId) {
      setError('No session');
      return;
    }

    // Call graph requires a symbol
    if (vizType === 'callgraph' && !targetSymbol) {
      setNodes([]);
      setEdges([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setNoDependencyData(false);

    try {
      let result;
      let transformed;

      switch (vizType) {
        case 'architecture':
          // Use directory granularity for architecture view
          // Depth 4 allows drill-down into nested directories
          result = await apiClient.getArchitecture(sessionId, {
            depth: 4,
            granularity: 'directory',
            inferModules: true,
            includeMetrics: true
          });

          // Detect module-level fallback for architecture view
          if (looksLikeModuleData(result.result)) {
            console.warn('[useGraphData] CKB returned module-level data for architecture view, retrying with noCache...');
            result = await apiClient.getArchitecture(sessionId, {
              depth: 4,
              granularity: 'directory',
              inferModules: true,
              includeMetrics: true,
              noCache: true
            });
          }

          // If still module-level after retry, handle per mode
          if (looksLikeModuleData(result.result)) {
            if (archMode === 'structure') {
              console.warn('[useGraphData] Architecture structure: falling back to file tree');
              const fileTreeResult = await apiClient.getFileTree(sessionId);
              transformed = transformFileTree(fileTreeResult.tree);
            } else {
              // Dependencies mode needs CKB import-scan data; file tree can't provide this
              transformed = { nodes: [], edges: [], noDependencyData: true };
            }
            break;
          }

          transformed = transformArchitecture(result.result, archMode);
          break;

        case 'callgraph':
          result = await apiClient.getCallGraph(sessionId, targetSymbol, { depth: 2 });
          transformed = transformCallGraph(result.result, targetSymbol);
          break;

        case 'files':
          // Use directory granularity with metrics for file explorer
          // Pass 'hierarchy' mode to get tree structure, not dependency edges
          result = await apiClient.getArchitecture(sessionId, {
            depth: 5,
            granularity: 'directory',
            inferModules: true,
            includeMetrics: true
          });

          // Detect module-level fallback — CKB may not have import-scan ready yet
          if (looksLikeModuleData(result.result)) {
            console.warn('[useGraphData] CKB returned module-level data for files view, retrying with noCache...');
            result = await apiClient.getArchitecture(sessionId, {
              depth: 5,
              granularity: 'directory',
              inferModules: true,
              includeMetrics: true,
              noCache: true
            });
          }

          // If still module-level after retry, fall back to file tree
          if (looksLikeModuleData(result.result)) {
            console.warn('[useGraphData] CKB still returning module data, falling back to file tree');
            const fileTreeResult = await apiClient.getFileTree(sessionId);
            transformed = transformFileTree(fileTreeResult.tree);
            break;
          }

          transformed = transformArchitecture(result.result, 'hierarchy');
          break;

        default:
          transformed = { nodes: [], edges: [] };
      }

      setNodes(transformed.nodes);
      setEdges(transformed.edges);
      if (transformed.noDependencyData) {
        setNoDependencyData(true);
      }
    } catch (err) {
      console.error(`Failed to fetch ${vizType} data:`, err);
      setError(err.message || 'Failed to load graph data');
      setNodes([]);
      setEdges([]);
    } finally {
      setLoading(false);
    }
  }, [sessionId, vizType, targetSymbol, archMode]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    nodes,
    edges,
    loading,
    error,
    noDependencyData,
    refetch: fetchData
  };
}

export default useGraphData;
